// Cave84 — Global Search (Phase 1 Week 6)
// Spec §4.8 F1.7. Fuzzy search across producer, wine, vintage year, notes.
// Client-side filter over TanStack Query caches. Three result sections:
//   1. Wines (catalog matches)
//   2. Bottles in cellar (inventory matches)
//   3. Producers

import { useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { WineStyle } from '@cave84/shared-types';
import { useWines } from '@/hooks/useWines';
import { useProducers, useProducersMap } from '@/hooks/useProducers';
import { useInventory, DEFAULT_FILTERS } from '@/hooks/useInventory';
import { foldSearch } from '@/utils/searchText';

// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------

const STYLE_DOT: Record<WineStyle, string> = {
  red: 'bg-claret', white: 'bg-amber-300', rose: 'bg-pink-400',
  sparkling: 'bg-sky-300', fortified: 'bg-orange-500',
  sweet: 'bg-yellow-300', orange: 'bg-orange-400',
};

// ---------------------------------------------------------------------------
// Section header
// ---------------------------------------------------------------------------

function SectionHeader({ title, count }: { title: string; count: number }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider">{title}</h2>
      <span className="text-xs text-text-muted">{count}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';

  // Focus once on mount. A `ref={el => el?.focus()}` callback re-fires on every
  // render and steals the caret back mid-typing.
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const { data: wines = [] } = useWines({});
  const { data: producers = [] } = useProducers();
  const producersMap = useProducersMap();

  // Inventory with no home filter so we search all locations
  const { rows: inventoryRows } = useInventory({
    ...DEFAULT_FILTERS,
    availableAtHome: false,
    search: '',
  });

  const term = foldSearch(q.trim());

  // Bottle matches (in-cellar only — inventory rows)
  const bottleMatches = useMemo(() => {
    if (!term) return [];
    return inventoryRows.filter(r => {
      const hay = foldSearch([
        r.producer.name,
        r.wine.cuvee_name ?? '',
        String(r.vintage.year),
        r.bottle.notes ?? '',
      ].join(' '));
      return hay.includes(term);
    }).slice(0, 10);
  }, [inventoryRows, term]);

  // Wine ids already surfaced by the "In Cellar" section. Every one of those
  // rows carries producer + cuvée, so a catalog hit on an owned wine is always
  // a duplicate of a row rendered just below it — drop it from the catalog list.
  const shownInCellar = useMemo(
    () => new Set(bottleMatches.map(r => r.wine.id)),
    [bottleMatches],
  );

  // Wine catalog matches — wines NOT currently represented in the cellar list.
  const wineMatches = useMemo(() => {
    if (!term) return [];
    return wines.filter(w => {
      if (shownInCellar.has(w.id)) return false;
      const producer = producersMap.get(w.producer_id)?.name ?? '';
      return (
        foldSearch(w.cuvee_name).includes(term) ||
        foldSearch(producer).includes(term)
      );
    }).slice(0, 10);
  }, [wines, producersMap, term, shownInCellar]);

  // Producer matches
  const producerMatches = useMemo(() => {
    if (!term) return [];
    return producers.filter(p =>
      foldSearch(p.name).includes(term) ||
      foldSearch(p.country).includes(term) ||
      foldSearch(p.region).includes(term)
    ).slice(0, 8);
  }, [producers, term]);

  const total = wineMatches.length + bottleMatches.length + producerMatches.length;

  return (
    <div className="min-h-screen bg-surface-0">
      {/* Sticky search bar */}
      <div className="sticky top-[56px] z-10 bg-surface-1 border-b border-border-medium px-4 py-3">
        <input
          type="text"
          value={q}
          onChange={e => setParams({ q: e.target.value }, { replace: true })}
          placeholder="Search wines, producers, vintages…"
          ref={inputRef}
          className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold text-sm"
        />
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5 space-y-7">
        {!term && (
          <p className="text-center text-text-muted text-sm py-10">
            Type to search wines, producers, vintages…
          </p>
        )}

        {term && total === 0 && (
          <p className="text-center text-text-muted text-sm py-10">
            No results for &ldquo;{q}&rdquo;
          </p>
        )}

        {/* ─── Wine catalog matches ─── */}
        {wineMatches.length > 0 && (
          <section>
            <SectionHeader title="Wine Catalog" count={wineMatches.length} />
            <div className="rounded-lg border border-border-medium overflow-hidden bg-surface-1 divide-y divide-border-subtle">
              {wineMatches.map(wine => {
                const producer = producersMap.get(wine.producer_id);
                return (
                  <Link
                    key={wine.id}
                    to={`/wines/${wine.id}`}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-surface-2 transition-colors group"
                  >
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STYLE_DOT[wine.style]}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-primary truncate">{wine.cuvee_name ?? '—'}</p>
                      {producer && <p className="text-xs text-text-muted truncate">{producer.name}</p>}
                    </div>
                    <svg className="w-4 h-4 text-text-muted group-hover:text-text-secondary flex-shrink-0"
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* ─── Inventory bottle matches ─── */}
        {bottleMatches.length > 0 && (
          <section>
            <SectionHeader title="In Cellar" count={bottleMatches.length} />
            <div className="rounded-lg border border-border-medium overflow-hidden bg-surface-1 divide-y divide-border-subtle">
              {bottleMatches.map(row => (
                <Link
                  key={row.bottle.id}
                  to={`/inventory/${row.bottle.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-surface-2 transition-colors group"
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STYLE_DOT[row.wine.style]}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <p className="text-sm font-medium text-text-primary truncate">{row.wine.cuvee_name ?? '—'}</p>
                      <span className="text-xs text-text-muted flex-shrink-0">{row.vintage.year}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-text-muted truncate">{row.producer.name}</p>
                      <span className="text-xs text-text-muted">·</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded border ${
                        row.cellar.is_home
                          ? 'bg-gold/10 border-gold/30 text-gold'
                          : 'bg-surface-3 border-border-medium text-text-muted'
                      }`}>
                        {row.cellar.is_home ? '🏠' : '✈️'} {row.cellar.name}
                      </span>
                    </div>
                  </div>
                  <svg className="w-4 h-4 text-text-muted group-hover:text-text-secondary flex-shrink-0"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* ─── Producer matches ─── */}
        {producerMatches.length > 0 && (
          <section>
            <SectionHeader title="Producers" count={producerMatches.length} />
            <div className="rounded-lg border border-border-medium overflow-hidden bg-surface-1 divide-y divide-border-subtle">
              {producerMatches.map(producer => (
                <Link
                  key={producer.id}
                  to={`/wines?producer=${producer.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-surface-2 transition-colors group"
                >
                  <div className="w-8 h-8 rounded-full bg-surface-3 border border-border-medium flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-text-secondary">
                      {producer.name.slice(0, 2).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">{producer.name}</p>
                    <p className="text-xs text-text-muted">
                      {[producer.country, producer.region].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <svg className="w-4 h-4 text-text-muted group-hover:text-text-secondary flex-shrink-0"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
